import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { CircularProgress, Tooltip } from "@mui/material";
import ShipeeziNewGrid, {
  Column,
  GeneralActions,
  Category,
} from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";
import { FiEdit, FiLink2 } from "react-icons/fi";
import { HiOutlineMail } from "react-icons/hi";
import { MdOutlineCancel } from "react-icons/md";
import { useHistory, useLocation } from "react-router-dom";

import { PRIVATE_ROUTES } from "../../constants";
import { useAppState } from "../../context/StateContext";
import useActionPurchaseOrder from "../../hooks/useActionPurchaseOrder";
import { Customer } from "../../models/customer";
import { PurchaseOrder } from "../../models/purchase.order";
import { Filters, Searches } from "../../models/shipeezi.table";
import {
  OrderFilterKeys,
  OrderShipByDate,
  OrderStatus,
  OrderType,
  PerformancePredictionType,
  UsedInEmailTemplates,
} from "../../util/enums";
import SendCommunicationModal, {
  SendCommunicationModalHandle,
} from "../Generic/SendCommunicationModal";
import { Job } from "../../models/job";
import { formatDateWithoutTimeZone } from "../../util/Format";
import { DefaultTooltip } from "../Generic/Tooltip";
import TitlePage from "../TitlePage";
import CancelOrderModal, { CancelOrderConfirmationModalHandles } from "./CancelOrderModal";
import { BookedByDate } from "./components/BookedByDate";
import getOrderOptions from "./getOrderOptions";
import StatusBadge from "./StatusBadge";
import useStyles from "./styles";
import PurchaseOrderSideView, { OrderViewHandles } from "./View";

interface OrdersMaintenanceLocationParams {
  status?: OrderStatus[];
  shipByDate?: OrderShipByDate[];
}

const Orders = () => {
  const classes = useStyles();
  const { currentUser, isFMVersion, isSupplierPortal, isSPVersion } = useAppState();
  const { t: translate } = useTranslation();
  const history = useHistory();
  const { state } = useLocation<OrdersMaintenanceLocationParams>();

  const { getAllPurchaseOrders, useCancelOrders } = useActionPurchaseOrder();

  const { orderStatusOptions } = getOrderOptions();

  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [searches, setSearches] = useState<Searches>([]);
  const [filters, setFilters] = useState<Filters>();

  const cancelModalRef = useRef<CancelOrderConfirmationModalHandles>(null);
  const sideViewRef = useRef<OrderViewHandles>(null);
  const communicationModalRef = useRef<SendCommunicationModalHandle>(null);

  const {
    data: purchaseOrdersData,
    isFetching: loadingPurchaseOrders,
    refetch: refetchPurchaseOrders,
  } = getAllPurchaseOrders(page, pageSize, searches, filters);

  useEffect(() => {
    if (purchaseOrdersData) {
      setPurchaseOrders(purchaseOrdersData?.rows);
      setTotalItems(purchaseOrdersData?.count);
    }
  }, [purchaseOrdersData]);

  const {
    mutate: cancelOrders,
    isLoading: isCancelingOrder,
    variables: cancelingOrderIds,
  } = useCancelOrders();

  const handleCurrentPage = (numPage: number) => {
    document.location.href = "#top";
    setPage(numPage - 1);
  };

  const handlePageSize = (pageSize: number) => {
    setPageSize(pageSize);
  };

  const handleFilter = (filters: Filters) => {
    setFilters(filters);
    setPage(0);
  };

  const handleSearch = (searches: Searches) => {
    setSearches(searches);
    setPage(0);
  };

  const renderReferenceFromIntegration = (reference: string, order: PurchaseOrder) => {
    if (!order.fromIntegration) return reference;

    return (
      <div className={classes.row}>
        <span>{reference}</span>
        <FiLink2 color="#38CFC4" size={15} />
      </div>
    );
  };

  const columns: Column[] = [
    {
      name: "reference",
      title: `${translate("order")} #`,
      searchIdentifier: "purchase_order_id",
      formatComponent: renderReferenceFromIntegration,
    },
    { name: "type", title: translate("type"), hideSearch: true },
    isSupplierPortal
      ? {
          name: "consignee",
          title: translate("consignee"),
          formatText: (consignee?: Customer) => consignee?.name || "",
          searchIdentifier: "consignee.name",
        }
      : {
          name: "supplierName",
          title: translate("supplier"),
          searchIdentifier: "supplier_name",
        },
    { name: "productTotalQuantity", title: translate("unitQty"), hideSearch: true },
    {
      name: "origin",
      title: translate("originPort"),
      searchIdentifier: "origin",
    },
    {
      name: "eta",
      title: translate("originalShipByDate"),
      formatText: (value?: string) => formatDateWithoutTimeZone(value),
      hideSearch: true,
    },
    {
      name: "currentEta",
      title: translate("currentShipByDate"),
      formatText: (value?: string) => formatDateWithoutTimeZone(value),
      hideSearch: true,
    },
    {
      name: "currentEta",
      title: translate("bookByDate"),
      formatComponent: (value: string, row: any) => {
        if (value) return <BookedByDate value={value} />;

        return <BookedByDate value={row.eta} />;
      },
      hideSearch: true,
    },
    {
      name: "destination",
      title: translate("destinationPort"),
      searchIdentifier: "destination",
    },
    {
      name: "status",
      title: translate("status"),
      formatComponent: (status: string) => (status ? <StatusBadge value={status} /> : null),
      hideSearch: true,
    },
  ];
  const filterCategories: Category[] = useMemo(
    () => [
      {
        name: translate("type"),
        options: OrderType && Object.values(OrderType),
      },
      ...(isFMVersion
        ? [
            {
              name: translate("performancePrediction"),
              options: PerformancePredictionType && Object.values(PerformancePredictionType),
            },
          ]
        : []),
      {
        name: translate("status"),
        options: orderStatusOptions.map((option) => {
          return {
            ...option,
          };
        }),
        optionsSelected: state?.status,
      },
      {
        name: translate("shipByDate"),
        column: OrderFilterKeys.SHIP_BY_DATE,
        options: [
          {
            label: translate("pastShipByDate"),
            value: "pastShipByDate",
          },
        ],
        optionsSelected: state?.shipByDate,
      },
    ],
    [state]
  );

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
      right: ["handleNewRecord"],
    },
    handleNewRecord: isSupplierPortal
      ? undefined
      : {
          text: translate("createOrder"),
          action: () =>
            history.push({
              pathname: PRIVATE_ROUTES.ORDERS_MNT,
              state: { viewOnly: false },
            }),
          tooltip: translate("create"),
        },
    handleFilterBy: {
      categories: filterCategories,
      onChange: handleFilter,
    },
    handleSearch: {
      onSearch: handleSearch,
    },
  };

  const editOrder = (id: string | number) => {
    history.push({
      pathname: `${PRIVATE_ROUTES.ORDERS_MNT}/${id}`,
      state: { viewOnly: false },
    });
  };
  const handleEditOrder = (id: number | string, row: PurchaseOrder) => {
    if (isSupplierPortal) return undefined;
    if (isFMVersion && currentUser?.companyId !== row.companyId) return undefined;
    if (row && row.status === OrderStatus.CONFIRMED) return undefined;

    return (
      <Tooltip data-cy="action-edit" title={translate("edit")} placement="top">
        <div>
          <FiEdit
            onClick={() => editOrder(id)}
            color="#475467"
            className={classes.button}
            size={18}
          />
        </div>
      </Tooltip>
    );
  };

  const viewOrder = (id: number | string) => {
    if (id && !Number.isNaN(Number(id))) sideViewRef.current?.open(Number(id));
  };

  const isOrderAvailableToCancel = useCallback((order: PurchaseOrder) => {
    if (order.status === OrderStatus.RECEIVED) return true;

    return false;
  }, []);

  const renderCancelOrderRowButton = useCallback(
    (id: number | string, row: PurchaseOrder) => {
      const orderId = Number(id);
      const openCancelModal = () => cancelModalRef?.current?.open(orderId, row.reference);

      if (!isOrderAvailableToCancel(row) || !isSPVersion) return null;

      if (isCancelingOrder && cancelingOrderIds?.includes(orderId)) {
        return <CircularProgress size={20} />;
      }

      return (
        <DefaultTooltip title={translate("cancel")} placement="top">
          <div>
            <MdOutlineCancel
              size={20}
              className={classes.button}
              data-cy="canceledButton"
              color="#F97066"
              onClick={() => {
                openCancelModal();
              }}
            />
          </div>
        </DefaultTooltip>
      );
    },
    [isSPVersion, isCancelingOrder]
  );

  const cancelOrderOnSubmit = (id: number) => {
    if (!id) return;

    cancelOrders([id], {
      onSuccess: () => {
        refetchPurchaseOrders();
        cancelModalRef.current?.close();
      },
    });
  };

  const handleCommunicationButton = useCallback(
    (id: string | number, row: PurchaseOrder) => {
      return (
        <Tooltip title={translate("communication")} placement="top">
          <div
            onClick={() => {
              communicationModalRef.current?.onOpen({
                entityType: "order",
                entityId: Number(row.id),
                entity: row,
                customer: row.consignee,
              });
            }}
            className={classes.button}
            data-cy="communication-button"
          >
            <HiOutlineMail size={18} color="#475467" />
          </div>
        </Tooltip>
      );
    },
    [translate, classes.button]
  );

  useEffect(() => {
    if (state) {
      const { status, shipByDate } = state;

      setFilters((prev) => {
        return {
          ...prev,
          status: status ?? [],
          [OrderFilterKeys.SHIP_BY_DATE]: shipByDate ?? [],
        };
      });
    }
  }, [state]);

  return (
    <div data-cy="purchaseOrdersListGrid">
      {/* <TitlePage title={translate("orders")} /> */}
      <div className={classes.ordersGridWrapper}>
        <ShipeeziNewGrid
          data={purchaseOrders}
          totalItems={totalItems}
          loadingData={loadingPurchaseOrders}
          namePage={translate("orders")}
          emptyData={
            loadingPurchaseOrders
              ? `${translate("loadingOrders")}...`
              : translate("noInformationToBeShown")
          }
          columns={columns}
          currentPage={page}
          pageSize={pageSize}
          pageSizeList={totalItems > pageSize ? [20, 40, 60, 100] : []}
          onChangePageSize={handlePageSize}
          onNextPage={handleCurrentPage}
          onPreviousPage={handleCurrentPage}
          onGoToPage={handleCurrentPage}
          generalActions={generalActions}
          rowActions={{
            orderBy: ["edit", "handleDetails", "cancel"],
            handleDetails: viewOrder,
          }}
          rowActionsExtra={[
            {
              title: translate("edit"),
              key: "edit",
              buttonAndAction: handleEditOrder,
            },
            {
              title: translate("communication"),
              key: "communication",
              buttonAndAction: handleCommunicationButton,
            },
            {
              title: translate("cancel"),
              key: "cancel",
              buttonAndAction: renderCancelOrderRowButton,
            },
          ]}
          positionActionsColumn="right"
          selecteds={selectedItems}
          handleSelect={setSelectedItems}
          showSelection={totalItems !== 0}
          showFooterGrid={totalItems !== 0}
        />
      </div>
      <CancelOrderModal
        ref={cancelModalRef}
        loadingAction={isCancelingOrder}
        onSubmit={cancelOrderOnSubmit}
      />
      <PurchaseOrderSideView ref={sideViewRef} />
      <SendCommunicationModal
        ref={communicationModalRef}
        usedIn={UsedInEmailTemplates.ORDERS}
        onRefetch={refetchPurchaseOrders}
      />
    </div>
  );
};

export default Orders;
